import { z } from 'zod';
import { dataHoraSchema, paginacaoSchema } from './comum.js';
import { cargoSchema, senhaOperadorSchema } from './auth.js';
/**
 * ============================================================================
 * COMERCIAL — a carteira do vendedor
 * ============================================================================
 *
 * Este dominio nasce inteiro aqui. O painel do vendedor rodava sobre
 * `mockData.ts` e sobre um servidor Express proprio que gravava num arquivo JSON
 * no disco do processo; nenhuma das quarenta chamadas que a tela fazia existia
 * neste contrato, e o formato de cada resposta era o que o handler daquele
 * momento devolvesse.
 *
 * TRES REGRAS QUE ATRAVESSAM O ARQUIVO INTEIRO
 *
 * 1. NENHUM SCHEMA TEM `vendedorId` DE ENTRADA. Nem em params, nem em query, nem
 *    em corpo. O servidor deriva o vendedor da SESSAO. A versao anterior aceitava
 *    `?sellerId=` com precedencia sobre o usuario logado em dez rotas — trocar o
 *    id na URL lia a carteira, as vendas e a comissao de outro vendedor. Aqui nao
 *    ha parametro para falsificar: ler dado de outra pessoa exige as rotas de
 *    `acesso: 'lideranca'`, que sao separadas de proposito.
 *
 * 2. TODA DATA E ISO COM OFFSET (`dataHoraSchema`), nunca "Hoje as 14:32" nem
 *    "29/08/2026". A tela filtrava por data com `String.includes('29/08')` — um
 *    filtro que para de funcionar no dia seguinte ao que foi escrito, sem erro. A
 *    formatacao pt-BR acontece so na borda de apresentacao.
 *
 * 3. TODA LISTAGEM E PAGINADA POR CURSOR. Nao existe array cru de resposta: as
 *    rotas antigas devolviam a tabela inteira, e o lead vinha com timeline,
 *    mensagens e ligacoes aninhadas dentro de cada item.
 */
// ------------------------------------------------------------------ enums --
/** Os treze estagios do funil. Antes o status era string livre, gravada sem
 *  validacao — qualquer valor entrava e envenenava todo agrupamento por etapa. */
export const statusLeadComercialSchema = z.enum([
    'novo',
    'abordado',
    'tentativa_1',
    'tentativa_2',
    'nao_atendeu',
    'respondeu',
    'interessado',
    'site_enviado',
    'negociacao',
    'fechado',
    'perdido',
    'sem_interesse',
    'remarketing',
]);
export const prioridadeLeadSchema = z.enum(['muito_quente', 'quente', 'morno', 'frio']);
export const resultadoLigacaoSchema = z.enum([
    'interessado',
    'nao_atendeu',
    'caixa_postal',
    'ocupado',
    'reagendou',
    'sem_interesse',
]);
export const statusVendaComercialSchema = z.enum(['pendente', 'confirmada', 'paga']);
export const metodoPagamentoVendaSchema = z.enum(['pix', 'cartao', 'boleto', 'parcelado']);
// ------------------------------------------------------------------- lead --
export const eventoLeadSchema = z.object({
    id: z.string(),
    tipo: z.string(),
    titulo: z.string(),
    descricao: z.string().nullable(),
    autorNome: z.string().nullable(),
    criadoEm: dataHoraSchema,
});
export const ligacaoLeadSchema = z.object({
    id: z.string(),
    duracaoSegundos: z.number().int().min(0),
    resultado: resultadoLigacaoSchema,
    tentativa: z.number().int().min(1),
    observacao: z.string().nullable(),
    criadoEm: dataHoraSchema,
});
export const mensagemLeadSchema = z.object({
    id: z.string(),
    texto: z.string(),
    ehLinkDeSite: z.boolean(),
    criadoEm: dataHoraSchema,
});
/**
 * O lead na LISTAGEM — a versao estreita.
 *
 * Sem timeline, sem mensagens, sem ligacoes. A resposta antiga trazia os tres
 * arrays aninhados dentro de cada item de uma lista sem limite: uma carteira de
 * 300 leads com 20 eventos cada arrastava 6.000 registros para desenhar uma
 * tabela que mostra nome, telefone e etapa. E a mesma licao de "tabela quente e
 * tabela estreita" que tirou o HTML do Postgres.
 */
export const leadComercialSchema = z.object({
    id: z.string(),
    codigo: z.string(),
    nome: z.string(),
    empresa: z.string().nullable(),
    telefone: z.string().nullable(),
    whatsapp: z.string().nullable(),
    cidade: z.string().nullable(),
    uf: z.string().nullable(),
    segmento: z.string().nullable(),
    origem: z.string().nullable(),
    status: statusLeadComercialSchema,
    prioridade: prioridadeLeadSchema,
    valorEstimado: z.number().nullable(),
    quenteDoDisparo: z.boolean(),
    notaInteresse: z.string().nullable(),
    siteUrl: z.string().nullable(),
    siteEnviadoEm: dataHoraSchema.nullable(),
    placeId: z.string().nullable(),
    vendedorId: z.string().nullable(),
    vendedorNome: z.string().nullable(),
    observacoes: z.string().nullable(),
    ultimoContatoEm: dataHoraSchema.nullable(),
    criadoEm: dataHoraSchema,
    /** Contadores derivados de tabela, com janela de data real — nao contadores
     *  acumulados que ninguem zera. `ligacoesHoje` significa hoje. */
    ligacoesHoje: z.number().int().min(0),
    ligacoesTotal: z.number().int().min(0),
    mensagensHoje: z.number().int().min(0),
});
/** O lead no DETALHE. Os arrays vem aqui, e so aqui. */
export const leadComercialDetalheSchema = leadComercialSchema.extend({
    eventos: z.array(eventoLeadSchema),
    ligacoes: z.array(ligacaoLeadSchema),
    mensagens: z.array(mensagemLeadSchema),
});
export const filtroLeadsComerciaisSchema = paginacaoSchema.extend({
    status: statusLeadComercialSchema.optional(),
    prioridade: prioridadeLeadSchema.optional(),
    busca: z.string().trim().min(1).max(120).optional(),
    /** `true` traz so quem o disparo marcou como interessado. */
    somenteQuentes: z.boolean().optional(),
    /** `true` traz so quem ja recebeu o link do site. */
    somenteComSite: z.boolean().optional(),
});
export const mudarStatusLeadSchema = z.object({
    status: statusLeadComercialSchema,
    observacao: z.string().max(2000).optional(),
});
export const registrarLigacaoSchema = z.object({
    duracaoSegundos: z.number().int().min(0).max(86_400),
    resultado: resultadoLigacaoSchema,
    observacao: z.string().max(2000).optional(),
    // `tentativa` NAO entra: e contada no servidor a partir das ligacoes gravadas.
    // Vinda do navegador, ela era sempre o que a tela achasse que era.
});
export const registrarMensagemSchema = z.object({
    texto: z.string().min(1).max(4000),
    ehLinkDeSite: z.boolean().default(false),
});
// ------------------------------------------------------------------ venda --
export const vendaComercialSchema = z.object({
    id: z.string(),
    codigo: z.string(),
    leadId: z.string().nullable(),
    clienteNome: z.string(),
    empresa: z.string().nullable(),
    produto: z.string(),
    valor: z.number(),
    percentualComissao: z.number(),
    valorComissao: z.number(),
    metodoPagamento: metodoPagamentoVendaSchema,
    status: statusVendaComercialSchema,
    observacao: z.string().nullable(),
    vendedorId: z.string(),
    vendedorNome: z.string().nullable(),
    criadoEm: dataHoraSchema,
});
export const filtroVendasSchema = paginacaoSchema.extend({
    status: statusVendaComercialSchema.optional(),
    periodo: z.enum(['hoje', '7d', '30d', 'todos']).default('30d'),
});
/**
 * Fechar venda.
 *
 * NAO recebe `percentualComissao` nem `valorComissao`. Eram calculados em quatro
 * lugares do navegador, todos com `30` literal, todos editaveis pelo DevTools de
 * quem recebe o pagamento. O percentual vem da configuracao do servidor e o valor
 * e derivado la; o navegador apenas exibe o que voltou.
 */
export const fecharVendaSchema = z.object({
    leadId: z.string().uuid(),
    produto: z.string().min(2).max(160),
    valor: z.number().positive().max(1_000_000),
    metodoPagamento: metodoPagamentoVendaSchema,
    observacao: z.string().max(2000).optional(),
});
// ----------------------------------------------------------------- resumo --
/**
 * O painel do vendedor, em numeros.
 *
 * Todo campo aqui e `count`/`sum` com janela de data real. Quando e zero, vem
 * zero: a versao anterior trocava zero por 184 ligacoes, R$ 28.800 de receita e
 * R$ 2.160 de comissao pendente, e a tela nao tinha como saber a diferenca entre
 * uma operacao ativa e um banco vazio.
 */
export const resumoVendedorSchema = z.object({
    leadsNaCarteira: z.number().int(),
    leadsContatadosHoje: z.number().int(),
    ligacoesHoje: z.number().int(),
    mensagensHoje: z.number().int(),
    sitesEnviadosHoje: z.number().int(),
    leadsInteressados: z.number().int(),
    leadsEmNegociacao: z.number().int(),
    vendasHoje: z.number().int(),
    vendasMes: z.number().int(),
    receitaHoje: z.number(),
    receitaMes: z.number(),
    comissaoHoje: z.number(),
    comissaoMes: z.number(),
    comissaoPendente: z.number(),
    comissaoPaga: z.number(),
    /**
     * Quantos da carteira ja foram fechados: leads em `fechado` / leads na carteira.
     *
     * Os dois lados sobre o MESMO conjunto, e por isso o numero nunca passa de
     * 100%. A versao anterior dividia as VENDAS DO MES pela CARTEIRA DE AGORA —
     * conjuntos diferentes, janelas diferentes: bastava um lead ser redistribuido
     * depois de fechado para o vendedor aparecer com 133% de conversao.
     *
     * `null` com carteira vazia. Divisao por zero nao e 0%, e "nao da para
     * calcular" — e quem ainda nao recebeu lead nao pode aparecer no painel do
     * lider ao lado de quem recebeu 200 e nao fechou nada.
     */
    taxaFechamentoCarteira: z.number().nullable(),
    porStatus: z.array(z.object({ status: statusLeadComercialSchema, total: z.number().int() })),
});
/** Quem esta logado, do ponto de vista deste app. */
export const identidadeComercialSchema = z.object({
    id: z.string(),
    nome: z.string(),
    email: z.email(),
    cargo: cargoSchema,
    telefone: z.string().nullable(),
    imagem: z.string().nullable(),
    ativo: z.boolean(),
    /** Derivado de `NIVEL_CARGO[cargo] <= NIVEL_CARGO.lider`, calculado no
     *  SERVIDOR. A tela nao decide o proprio nivel de acesso. */
    ehLideranca: z.boolean(),
    /**
     * Pode cadastrar e alterar vendedor — derivado de
     * `NIVEL_CARGO[cargo] <= NIVEL_CARGO.ceo`, que e EXATAMENTE o corte do
     * `case 'admin'` do guard.
     *
     * Existe pelo mesmo motivo de `ehLideranca`: a aba de equipe precisa aparecer
     * para quem pode usa-la e sumir para quem nao pode, e essa decisao nao pode
     * ser um `cargo === 'admin'` escrito no navegador. Um segundo lugar
     * calculando o mesmo corte e um segundo lugar para ele divergir do guard — e
     * quando divergir, a tela oferece um formulario que o servidor recusa com 403
     * sem explicar por que.
     *
     * Note que ele NAO substitui o guard: e so o que a tela mostra. Quem recusa
     * de verdade e `acesso: 'admin'` nas rotas.
     */
    podeGerirEquipe: z.boolean(),
    criadoEm: dataHoraSchema,
});
// -------------------------------------------------------------- lideranca --
export const vendedorResumidoSchema = z.object({
    id: z.string(),
    nome: z.string(),
    email: z.email(),
    cargo: cargoSchema,
    telefone: z.string().nullable(),
    imagem: z.string().nullable(),
    ativo: z.boolean(),
    leadsNaCarteira: z.number().int(),
    ligacoesMes: z.number().int(),
    sitesEnviadosMes: z.number().int(),
    vendasMes: z.number().int(),
    receitaMes: z.number(),
    comissaoMes: z.number(),
    /** `null` quando a carteira esta vazia. Ver `taxaConversaoMes`. */
    /** Ver `taxaFechamentoCarteira` em `resumoVendedorSchema`: mesma definicao. */
    taxaFechamentoCarteira: z.number().nullable(),
    ultimaAtividadeEm: dataHoraSchema.nullable(),
});
export const resumoLiderancaSchema = z.object({
    leadsTotal: z.number().int(),
    leadsSemDono: z.number().int(),
    vendedoresAtivos: z.number().int(),
    ligacoesHoje: z.number().int(),
    sitesEnviadosHoje: z.number().int(),
    vendasHoje: z.number().int(),
    vendasMes: z.number().int(),
    receitaHoje: z.number(),
    receitaMes: z.number(),
    comissaoPendente: z.number(),
    comissaoPaga: z.number(),
    /** Leads em `fechado` sobre o total de leads da base. Mesma definicao de
     *  `taxaFechamentoCarteira`, aplicada a operacao inteira. */
    taxaFechamentoGeral: z.number().nullable(),
    porStatus: z.array(z.object({ status: statusLeadComercialSchema, total: z.number().int() })),
    /**
     * O que esta tela NAO devolve, e por que.
     *
     * Ramp-up (dias ate a primeira venda, dias ate consistencia), churn e ROI
     * saiam da versao anterior como numero cheio e nao eram calculados: ramp-up era
     * uma cascata de `if (sellerId === '<id fixo>')`, churn era o literal 6.8 em
     * quatro lugares, e ROI tinha tres formulas incompativeis no mesmo arquivo
     * (dividindo por 12000, 3600 e 4500) sem nenhuma definicao escrita de custo.
     *
     * Nao ha fonte no banco para nenhum dos tres: falta data de admissao, falta
     * historico DIARIO de conversao por vendedor e falta a definicao de custo. Um
     * numero inventado num painel de gestao e pior que um campo ausente — ele e
     * usado para decidir sobre pessoas. Entram quando a fonte existir.
     */
    metricasIndisponiveis: z.array(z.string()),
});
export const paramsVendedorSchema = z.object({ id: z.string() });
/** Distribuir leads sem dono para um vendedor. E a rota que da carteira a alguem. */
export const distribuirLeadsSchema = z.object({
    vendedorId: z.string(),
    quantidade: z.number().int().min(1).max(200),
    /** Sem filtro, pega os mais antigos sem dono. Com, respeita a prioridade. */
    somenteQuentes: z.boolean().default(false),
});
export const resultadoDistribuicaoSchema = z.object({
    atribuidos: z.number().int(),
    vendedorId: z.string(),
});
/**
 * ============================================================================
 * DISTRIBUICAO EM LOTE — N leads para cada um de varios vendedores
 * ============================================================================
 *
 * Por que uma rota nova e nao um laco de `distribuir` no navegador:
 *
 * 1. UM LOTE, UMA TRANSACAO. Cinco chamadas separadas sao cinco transacoes: a
 *    terceira pode falhar com as duas primeiras ja gravadas, e quem distribuiu
 *    fica sem saber quem recebeu. Aqui ou o lote inteiro entra, ou nada entra.
 *
 * 2. A FILA E A MESMA PARA TODOS. Cinco chamadas concorrentes leem a MESMA fila
 *    de leads sem dono; a garantia de `updateMany ... WHERE vendedor_id IS NULL`
 *    impede o lead de ir para duas pessoas, mas o resultado vira uma corrida:
 *    quem chegou antes leva, e o pedido de quem chegou depois volta menor sem
 *    explicacao. Num lote so, o servidor reserva em ordem e cada destino sabe
 *    exatamente quanto sobrou para ele.
 *
 * 3. VALIDACAO ANTES DE ESCREVER. Um id invalido no meio de um laco no navegador
 *    ja deixou os anteriores gravados. Aqui todos os destinos sao conferidos
 *    ANTES da primeira escrita, e a mensagem de erro nomeia TODOS os problemas
 *    de uma vez — a diferenca entre uma correcao e cinco tentativas.
 */
export const destinoDistribuicaoSchema = z.object({
    vendedorId: z.string(),
    /** Por destino, nao para o lote: distribuir 30 para um e 10 para outro e o
     *  caso normal quando as carteiras estao desiguais. */
    quantidade: z.number().int().min(1).max(200),
});
/** Teto de leads que um unico lote pode mover. Ver o `superRefine` abaixo. */
export const LIMITE_LEADS_POR_LOTE = 2000;
export const distribuirEmLoteSchema = z
    .object({
    destinos: z.array(destinoDistribuicaoSchema).min(1).max(50),
    somenteQuentes: z.boolean().default(false),
})
    .superRefine((d, ctx) => {
    /*
     * O MESMO VENDEDOR DUAS VEZES E RECUSADO, e nao somado.
     *
     * Somar seria a escolha "gentil" e a errada: com dois destinos iguais a
     * resposta teria duas linhas para a mesma pessoa, e nenhuma leitura possivel
     * de "pedi 10 e recebi 4" — 4 de qual das duas linhas? Uma tela que monta o
     * corpo a partir de uma lista de selecao nunca produz isso de proposito;
     * quando produz, e bug dela, e o lugar de descobrir e aqui.
     */
    const vistos = new Set();
    for (const [i, destino] of d.destinos.entries()) {
        if (vistos.has(destino.vendedorId)) {
            ctx.addIssue({
                code: 'custom',
                path: ['destinos', i, 'vendedorId'],
                message: 'o mesmo vendedor aparece duas vezes na lista de destinos',
            });
        }
        vistos.add(destino.vendedorId);
    }
    /*
     * Teto do LOTE, alem do teto por destino.
     *
     * 50 destinos x 200 cada sao 10.000 leads movidos numa transacao unica, mais
     * 10.000 eventos de timeline. Isso passa do tempo limite da transacao
     * interativa e derruba o lote inteiro DEPOIS de segurar a tabela mais quente
     * do sistema — e a tela nao teria como distinguir isso de "nao havia leads".
     * O teto e menor de proposito: um lote recusado na validacao custa uma
     * mensagem; um lote recusado no meio da transacao custa a janela toda.
     */
    const total = d.destinos.reduce((s, x) => s + x.quantidade, 0);
    if (total > LIMITE_LEADS_POR_LOTE) {
        ctx.addIssue({
            code: 'custom',
            path: ['destinos'],
            message: `o lote soma ${total} leads e o teto e ${LIMITE_LEADS_POR_LOTE}; divida em mais de uma distribuicao`,
        });
    }
});
export const resultadoDistribuicaoLoteSchema = z.object({
    /** Uma linha POR DESTINO, com o pedido ao lado do que de fato foi atribuido.
     *  Sem `pedidos` na resposta, "recebeu 4" nao diz se foi sucesso ou falta. */
    destinos: z.array(z.object({
        vendedorId: z.string(),
        vendedorNome: z.string(),
        pedidos: z.number().int(),
        atribuidos: z.number().int(),
    })),
    totalPedido: z.number().int(),
    totalAtribuido: z.number().int(),
    /** Quantos leads sem dono sobraram na fila depois do lote. E o numero que diz
     *  se o "atribuidos < pedidos" foi por falta de lead ou por outra coisa. */
    leadsSemDonoRestantes: z.number().int(),
});
// ------------------------------------------------------ gestao da equipe --
/**
 * Os cargos que este painel pode CRIAR e ATRIBUIR.
 *
 * Deliberadamente menor que `cargoSchema`. A rota de cadastro deste painel e
 * `acesso: 'admin'`, entao quem a alcanca ja poderia criar um admin por
 * `usuarios.criar` — nao ha escalada de privilegio aqui. O que ha e o principio
 * de menor superficie: a tela do comercial cadastra quem trabalha carteira, e
 * um `cargo` livre no corpo transforma um formulario de vendedor num caminho
 * para criar CEO por engano, com o mesmo clique.
 *
 * Casa com `CARGOS_DE_CAMPO` do servidor, que e quem decide se alguem pode
 * receber lead. Divergir os dois criaria um cadastro que nasce sem poder receber
 * carteira nenhuma.
 */
export const cargoDeCampoSchema = z.enum(['vendedor', 'lider']);
/**
 * A MESMA lista, como array, para o servidor.
 *
 * Derivada do enum acima e nao escrita de novo: ela e consultada em tres lugares
 * do servidor com significados que precisam coincidir — quem pode RECEBER lead
 * (`exigirVendedorDeCampo`), quem APARECE no painel da equipe
 * (`comercial.lideranca.vendedores`) e quem PERDE a carteira ao sair de operacao
 * (`atualizarOperador`). Se a terceira divergir das duas primeiras, existe um
 * cargo que aparece na tela, recebe lead e nunca devolve — o lead fica preso numa
 * conta que ninguem abre.
 *
 * Vive no contrato, e nao num dos servicos, porque o segundo servico a precisar
 * dela ja apareceu: `auth.service.ts` nao pode importar de `comercial.service.ts`
 * sem inverter a direcao das dependencias.
 */
export const CARGOS_DE_CAMPO = cargoDeCampoSchema.options;
/**
 * O teste que os dois servicos fazem, com o tipo certo.
 *
 * Existe porque `CARGOS_DE_CAMPO` e uma tupla `readonly ['vendedor','lider']` —
 * e `.includes(cargo)` com um `Cargo` qualquer nao compila contra ela. A saida
 * facil seria alargar a constante para `Cargo[]`, o que devolveria o problema
 * anterior: nada avisaria se alguem escrevesse `CARGOS_DE_CAMPO.push('gestora')`.
 * A tupla estreita fica, e o alargamento acontece aqui, uma vez.
 */
export function ehCargoDeCampo(cargo) {
    return CARGOS_DE_CAMPO.includes(cargo);
}
/** Telefone do vendedor. Formato livre de proposito: o painel e usado por gente
 *  que digita com DDD, com +55, com e sem hifen, e recusar isso nao protege
 *  nada. O que ele NAO aceita e string vazia disfarcada de telefone. */
export const telefoneOperadorSchema = z.string().trim().min(8).max(24);
export const criarVendedorSchema = z.object({
    nome: z.string().trim().min(2).max(120),
    email: z.email(),
    /** Mesma regra de `usuarios.criar`, importada — nao reescrita. */
    senha: senhaOperadorSchema,
    cargo: cargoDeCampoSchema.default('vendedor'),
    telefone: telefoneOperadorSchema.optional(),
    /** Superior direto. `usuarios.criar` ja aceita; aqui existe para o lider poder
     *  nascer ligado a quem responde por ele. */
    gestorId: z.string().optional(),
});
/**
 * Alterar um vendedor.
 *
 * NAO tem `email` nem `senha` de proposito. Email e a chave de login e a chave
 * natural da conta no Better Auth; troca-lo pelo painel comercial e uma operacao
 * de identidade, nao de gestao de equipe. Senha idem: quem esqueceu a senha
 * passa pelo fluxo de senha, nao por um campo num formulario de cadastro que o
 * lider deixa aberto na tela.
 */
export const alterarVendedorSchema = z
    .object({
    nome: z.string().trim().min(2).max(120).optional(),
    /** `null` apaga o telefone; ausente nao mexe nele. Sao coisas diferentes, e
     *  colapsa-las faria "nao mandei telefone" apagar o que ja estava la. */
    telefone: telefoneOperadorSchema.nullable().optional(),
    cargo: cargoDeCampoSchema.optional(),
    ativo: z.boolean().optional(),
})
    /*
     * `gestorId` NAO entra, e a ausencia e deliberada.
     *
     * Hierarquia (quem responde a quem) e do painel de equipe, nao do painel de
     * vendas: `usuarios.atualizar` ja a trata. E ela e o unico campo cujo valor
     * invalido nao produz mensagem util aqui — um id inexistente so seria pego
     * pela chave estrangeira, virando um 409 generico de "referencia invalida"
     * que nao diz qual campo estava errado.
     */
    .refine((d) => Object.values(d).some((v) => v !== undefined), {
    message: 'informe pelo menos um campo para alterar',
});
/**
 * O resultado de alterar um vendedor.
 *
 * `leadsLiberados` nao e enfeite: desativar alguem DEVOLVE a carteira dele para
 * a fila comum, na mesma transacao, e esse numero e a unica forma de a tela
 * dizer o que aconteceu. Sem ele, um clique em "desativar" moveria dezenas de
 * leads em silencio.
 *
 * O motivo de devolver: um lead preso numa conta desativada nao esta "sem dono"
 * — entao nao aparece em `leadsSemDono`, nao entra em distribuicao nenhuma e o
 * dono nao pode abrir o painel. Ele some da operacao sem sumir do banco, que e
 * exatamente a perda silenciosa que `exigirVendedorDeCampo` ja evitava na
 * entrada e ninguem evitava na saida.
 */
export const resultadoAlteracaoVendedorSchema = z.object({
    vendedor: vendedorResumidoSchema,
    leadsLiberados: z.number().int(),
});
/**
 * A ponte captacao -> comercial.
 *
 * Promove um `Lead` da varredura do Google Maps (PK `place_id`) para um
 * `LeadComercial` trabalhavel. As duas tabelas continuam separadas: a varredura
 * precisa poder reprocessar a mesma regiao sem sobrescrever o trabalho de
 * ninguem, e um lead comercial precisa de dono, etapa e historico que a varredura
 * nao pode ter. `placeId` fica gravado no destino para nao promover duas vezes o
 * mesmo estabelecimento.
 */
export const promoverLeadsCaptacaoSchema = z.object({
    placeIds: z.array(z.string()).min(1).max(200),
    /** Opcional: ja entrega a carteira a alguem. Sem isso, entram sem dono. */
    vendedorId: z.string().optional(),
});
export const resultadoPromocaoSchema = z.object({
    promovidos: z.number().int(),
    jaExistiam: z.number().int(),
    naoEncontrados: z.number().int(),
});
// ----------------------------------------------------------- configuracao --
/**
 * O percentual de comissao vigente.
 *
 * Existe como rota porque a coluna nasceu sem ela: `ConfigGlobal.percentualComissao`
 * foi criada para tirar o `30` literal de quatro lugares do navegador, e ate aqui
 * NENHUMA rota a lia ou escrevia. O efeito pratico era pior que o problema
 * original: a taxa que decide quanto a empresa paga so podia ser mudada por SQL
 * direto em producao, sem registro de quem mudou e sem a lideranca conseguir nem
 * conferir qual e o valor em vigor.
 */
export const configComercialSchema = z.object({
    percentualComissao: z.number(),
});
export const definirComissaoSchema = z.object({
    /**
     * Em pontos percentuais (30 = 30%). Teto de 100 porque comissao maior que a
     * venda nao e politica comercial, e minimo acima de zero porque zerar a taxa
     * pela interface e mais provavel como acidente que como decisao.
     */
    percentualComissao: z.number().min(0.01).max(100),
});
//# sourceMappingURL=comercial.js.map